import { Helmet } from 'react-helmet';
import config from '../../../config/config.json';
import { AppManifest } from '../../../utils/AppManifest';
import { getEnvConfig } from '../../../utils/envConfig';
import { Pages, PageType } from '../../../utils/Pages';

import {
  Corporation,
  Country,
  GovernmentOrganization,
  GovernmentService,
  Graph,
  IdReference,
  ListItem,
  WebPage,
  WebSite,
} from 'schema-dts';

/* Structured Data Info
    - https://schema.org
    - https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data
    - https://www.bing.com/webmasters/help/marking-up-your-site-with-structured-data-3a93e731
    - https://github.com/google/react-schemaorg
    - https://www.npmjs.com/package/schema-dts and https://github.com/google/schema-dts
    - https://developers.google.com/search/docs/appearance/site-names#content-guidelines

  Validation Tools
  - https://validator.schema.org/
  - https://search.google.com/test/rich-results

  Notes:
  - Organization and sub-types require either ContactPoint (can be empty) or Address to be recognised by Google, even though these elements are optional in the schema.
*/

const envConfig = getEnvConfig();
const siteUrl = `https://${AppManifest.Hostname}`;
const keywords = 'SmartCard, SmartCard merchant, plb, product level blocking, product level blocking merchant, enhanced Income Management, income support payment, restricted items, restricted products, welfare reform, Services Australia merchant, welfare card, welfare debit card, basicscard, cashless card, cashless debit card, basics card, CDC';
const copyrightYear = (new Date(AppManifest.LastPublishedOn)).getFullYear();

const agencyID = `${siteUrl}/#agency`;
const vendorID = `${siteUrl}/#vendor`;
const serviceID = `${siteUrl}/#service`;
const websiteID = `${siteUrl}/#site`;

const about: GovernmentService = {
  '@type': 'GovernmentService',
  name: 'Product Level Blocking (PLB) portal for merchants'
}

const australia: Country = {
  '@type': 'Country',
  name: 'AU'
}

const homepageBreadcrumb: ListItem = {
  '@type': 'ListItem',
  position: 1,
  name: Pages[PageType.Home].title,
  item: {
    '@type': 'WebPage',
    '@id': siteUrl
  }
}

const refAgency: IdReference = {
  '@id': agencyID
};

const refVendor: IdReference = {
  '@id': vendorID
};

const refWebsite: IdReference = {
  '@id': websiteID
};

const agency: GovernmentOrganization = {
  '@type': 'GovernmentOrganization',
  '@id': agencyID,
  name: 'Product Level Blocking Program',
  url: config.SA_WEBSITE,
  areaServed: australia,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: `+61 ${config.CONTACT_NUMBER}`,
    email: config.ServicesAustralia_EMAIL
  },
  parentOrganization: {
    '@type': 'GovernmentOrganization',
    name: config.ServicesAustralia_ORG,
    url: config.SA_WEBSITE,
    contactPoint: {
      '@type': 'ContactPoint',
      areaServed: australia,
    }/*,
    sameAs: [
      'https://www.facebook.com/ServicesAustralia',
      'https://www.instagram.com/services.australia',
      'https://www.youtube.com/channel/UCg5fHRiVL9fvAgQ-OP2FyHw',
      'https://au.linkedin.com/company/services-australia',
      'https://en.wikipedia.org/wiki/Services_Australia'
    ],
    */
  }
};

const vendor: Corporation = {
  '@type': 'Corporation',
  '@id': vendorID,
  name: AppManifest.Authors,
  url: 'https://www.dxc.com',
  contactPoint: {
    '@type': 'ContactPoint',
    email: AppManifest.AuthorsEmail
  },
  parentOrganization: {
    '@type': 'Corporation',
    name: 'DXC Technology Australia',
    url: 'https://www.dxc.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '26 Talavera Road',
      addressLocality: 'Macquarie Park',
      addressRegion: 'NSW',
      postalCode: '2113',
      addressCountry: australia
    }/*,
    sameAs: [
      'https://www.linkedin.com/company/dxctechnology',
      'https://www.instagram.com/dxctechnology',
      'https://www.youtube.com/@dxctechnology',
      'https://en.wikipedia.org/wiki/DXC_Technology'
    ]
    */
  }
};

const service: GovernmentService = {
  '@type': 'GovernmentService',
  '@id': serviceID,
  name: 'Product Level Blocking',
  alternateName: 'PLB',
  description: 'A function on the POS system to ensure restricted items cannot be purchased with a SmartCard. The POS system and EFTPOS terminal work together to identify when a SmartCard is presented by a customer, and if there are any restricted items in the sale.',
  serviceOperator: [
    refAgency,
    refVendor
  ],
  areaServed: australia,
  isRelatedTo: [
    {
      '@type': 'GovernmentService',
      name: 'SmartCard information for businesses',
      description: 'Services Australia page for SmartCard information for businesses',
      url: 'https://www.servicesaustralia.gov.au/smartcard-information-for-businesses'
    },
    {
      '@type': 'GovernmentService',
      name: 'Enhance income management and smartcard',
      description: 'Services Australia page for enhance income management and smartcard',
      url: 'https://www.servicesaustralia.gov.au/enhanced-income-management-and-smartcard'
    }
  ],
  serviceOutput: [
    refWebsite,
    {
      '@type': 'WebSite',
      name: 'PLB Portal',
      description: 'PLB portal for stakeholders',
      url: 'https://home.plbportal.com.au/'
    }
  ]
};

const webSite: WebSite = {
  '@type': 'WebSite',
  '@id': websiteID,
  name: 'PLB Merchant Portal',
  about: about,
  url: siteUrl,
  publisher: refVendor,
  author: refAgency,
  copyrightHolder: refAgency,
  copyrightYear: copyrightYear,
  audience: {
    '@type': 'BusinessAudience',
    name: 'Merchants'
  },
  countryOfOrigin: australia,
  inLanguage: 'en-AU',
  datePublished: AppManifest.LastPublishedOn,
  keywords: keywords
};

const createWebPage = (page: PageType, pageUrl: string): WebPage => ({
  '@type': 'WebPage',
  '@id': pageUrl,
  name: 'PLB Merchant Portal',
  about: about,
  description: Pages[page].description,
  url: pageUrl,
  publisher: refVendor,
  author: refAgency,
  copyrightHolder: refAgency,
  copyrightYear: copyrightYear,
  datePublished: AppManifest.LastPublishedOn,
  isPartOf: [refWebsite]
});

const addHomePage = (page: PageType, pageUrl: string): Graph => {
  const webPage = createWebPage(page, pageUrl);

  webPage.breadcrumb = {
    '@type': 'BreadcrumbList',
    itemListElement: [homepageBreadcrumb]
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      agency,
      vendor,
      service,
      webSite,
      webPage
    ]
  };
};

const addPage = (page: PageType, pageUrl: string): Graph => {
  const webPage = createWebPage(page, pageUrl);

  webPage.breadcrumb = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      homepageBreadcrumb,
      {
        '@type': 'ListItem',
        position: 2,
        name: Pages[page].title,
        item: {
          '@type': 'WebPage',
          '@id': pageUrl
        }
      }
    ]
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      webPage
    ]
  };
};

export const AppHelmet = {
  Add: (page: PageType) => {
    console.log(`[AppHelmet] Title: '${Pages[page].title}'; Page: ${page}`);

    const pageUrl: string = `${siteUrl}/${Pages[page].url}`;
    console.log(`[AppHelmet] Url: ${pageUrl}`);

    let graph: Graph;
    if (page === PageType.Home) {
      graph = addHomePage(page, pageUrl);
    } else {
      graph = addPage(page, pageUrl);
    }

    const jsonSchema = JSON.stringify(graph);

    return (
      <Helmet>
        <title>{Pages[page].title}</title>
        <meta name="author" content={AppManifest.Company} />
        <meta name="creator" content={AppManifest.Authors} />
        <meta name="version" content={AppManifest.Version} />
        <meta name="robots" content={envConfig.WebMetadataRobots} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">{jsonSchema}</script>
      </Helmet>
    );
  }
}
